import React, { ReactEventHandler, RefObject, useCallback, useDebugValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { CenteredContainer, Loading } from '.';
//import { useTranslation } from 'react-i18next';
import styled from '../themes/themed-styled-components';

const AssetViewerContainer = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
`;

const AssetViewerOverlay = styled(CenteredContainer)`
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${({ theme: { white } }) => white};
`;

const AssetViewerIframe = styled.iframe`
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    overflow: auto;
    display: block;
`;

export type AssetViewerError = 'notfound' | 'request' | 'server' | 'unknown';

export type AssetViewerLoadingHandler = (src: string) => void
export type AssetViewerLoadedHandler = (iframe: HTMLIFrameElement) => void
export type AssetViewerErrorHandler = (error: AssetViewerError) => void

export type AssetViewerProps = {
    src: string,
    className?: string,
    onLoading?: AssetViewerLoadingHandler,
    onLoaded?: AssetViewerLoadedHandler,
    onError?: AssetViewerErrorHandler
}


const AssetViewer = ({ src, className, onLoading, onLoaded, onError }: AssetViewerProps, ref: RefObject<HTMLIFrameElement> | null = null) => {
    const [iframeSrc, setIframeSrc] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<AssetViewerError|null>(null);
    const { t } = useTranslation('common');

    useEffect(() => {
        if (error === null) {
            return;
        }
        setLoading(false);
        onError?.(error);
    }, [error]);

    useEffect(() => {
        setLoading(true);
        onLoading?.(src);
        setError(null);
        setIframeSrc('about:blank');

        let abort = false;

        const controller = new AbortController();
        const { signal } = controller;

        fetch(src, { signal }).then((res) => {
            setLoading(false);

            if (abort === true) {
                return;
            }

            switch (res.status) {
                case 200:
                    setIframeSrc(src);
                    break;
                case 404:
                    setError('notfound');
                    break;
                case 500:
                    setError('server');
                    break;
                default:
                    setError('unknown');
            }
        }).catch((err) => {
            if (abort === true || err.name === "AbortError") {
                return;
            }
            setError('request');
        });

        return () => {
            controller.abort();
            abort = true;
        }
    }, [src, onLoading, setIframeSrc, setError]);

    const handleIframeLoad: ReactEventHandler<HTMLIFrameElement> = useCallback((ev) => {

        const iframe = ev.target as HTMLIFrameElement;

        if (iframe.src === 'about:blank') {
            return;
        }

        onLoaded?.(iframe);

    }, [onLoaded]);

    const retry = useCallback(() => {
        setIframeSrc('about:blank');
    }, [setIframeSrc]);

    const overlayContent = useMemo(() => {
        if (loading === true) {
            return <Loading />
        }

        if (error === null) {
            return null;
        }

        switch (error) {
            case 'notfound':
                return <p>{t('Documento non trovato.')}</p>;
            case 'request':
                return <>
                    <p>{t('Si è verificato un errore durante la richiesta.')}</p>
                    {/*<Button onClick={retry}>{t('Riprova')}</Button>*/}
                </>
            default:
                return <>
                    <p>{t('Si è verificato un errore.')}</p>
                    {/*<Button onClick={retry}>{t('Riprova')}</Button>*/}
                </>;
        }

    }, [loading, error, t, retry])

    return <AssetViewerContainer>
        {(overlayContent !== null) ? <AssetViewerOverlay>{overlayContent}</AssetViewerOverlay> : null}
        <AssetViewerIframe className={className} src={iframeSrc} onLoad={handleIframeLoad} />
    </AssetViewerContainer>;
}

export default AssetViewer;